import React from 'react';

// Status label mapping — "active"/"healthy" → "Live", everything pending → "Pending"
const statusLabels = {
  active: 'Live',
  healthy: 'Live',
  deployed: 'Live',
  running: 'Live',
  completed: 'Live',
  inactive: 'Pending',
  pending: 'Pending',
  pending_approval: 'Pending',
  stopped: 'Stopped',
  degraded: 'Degraded',
  failed: 'Failed',
  critical: 'Critical',
  warning: 'Warning',
  rejected: 'Rejected',
  blocked: 'Blocked',
  // Workflow lifecycle — surface the literal stage name so it reads as
  // "this workflow is in Draft" (not the generic "Pending").
  draft: 'Draft',
  paused: 'Paused',
  archived: 'Archived',
};

const statusColors = {
  active: { bg: '#0d9488', text: '#ffffff' },
  inactive: { bg: '#a8a29e', text: '#ffffff' },
  pending: { bg: '#a8a29e', text: '#ffffff' },
  deployed: { bg: '#0d9488', text: '#ffffff' },
  failed: { bg: '#991b1b', text: '#ffffff' },
  running: { bg: '#0d9488', text: '#ffffff' },
  stopped: { bg: '#78716c', text: '#ffffff' },
  healthy: { bg: '#0d9488', text: '#ffffff' },
  degraded: { bg: '#d97706', text: '#ffffff' },
  critical: { bg: '#991b1b', text: '#ffffff' },
  warning: { bg: '#d97706', text: '#ffffff' },
  low: { bg: '#a8a29e', text: '#ffffff' },
  medium: { bg: '#d97706', text: '#ffffff' },
  high: { bg: '#991b1b', text: '#ffffff' },
  completed: { bg: '#0d9488', text: '#ffffff' },
  pending_approval: { bg: '#a8a29e', text: '#ffffff' },
  rejected: { bg: '#991b1b', text: '#ffffff' },
  blocked: { bg: '#991b1b', text: '#ffffff' },
  // Workflow lifecycle palette — Draft/Paused are dialled-back greys so
  // the eye doesn't read them as alarms; Archived is darker still.
  draft:    { bg: '#a8a29e', text: '#ffffff' },
  paused:   { bg: '#d97706', text: '#ffffff' },
  archived: { bg: '#57534e', text: '#ffffff' },
};

/**
 * Badge — accepts EITHER a `status` prop (for the lifecycle / health
 * vocabulary above) OR a `text` prop (free-form label, used in places
 * like the workflows list table). Both old and new call sites work.
 *
 * The `text` form was being used silently with no rendering — leaving
 * a blank cell — because the props weren't wired. Accepting it
 * explicitly fixes those call sites without making them rewrite.
 */
export function Badge({ status, text, children, variant = 'solid', useLabel = true }) {
  // If the caller passed text= but not status=, treat the text as the
  // status key for lookup purposes; that recovers the "draft" colour for
  // <Badge text={wf.status} />-style call sites.
  const effectiveStatus = status || text;
  const colors = statusColors[effectiveStatus] || { bg: '#78716c', text: '#ffffff' };
  const label = useLabel
    ? (statusLabels[effectiveStatus] || children || effectiveStatus)
    : (children || effectiveStatus);

  // Variant resolution: 'outline' is the only "no fill" mode; anything
  // else — including legacy call-site values like 'info' / 'success' /
  // 'neutral' that this component never officially supported — renders as
  // a filled pill. Without this fallback those legacy variants silently
  // produced an invisible (transparent) background, which is exactly the
  // "where did my Status badge go?" bug we keep stepping on.
  const isOutline = variant === 'outline';

  return (
    <span
      style={{
        display: 'inline-block',
        padding: '4px 10px',
        borderRadius: 4,
        fontSize: 12,
        fontWeight: 500,
        fontFamily: "'IBM Plex Mono', monospace",
        background: isOutline ? 'transparent' : colors.bg,
        color: isOutline ? colors.bg : colors.text,
        border: isOutline ? `1px solid ${colors.bg}` : 'none',
        whiteSpace: 'nowrap',
      }}
    >
      {label}
    </span>
  );
}
